import { Form, notification } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import {
  useLazyGetPositionsForSystemQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import { useUpdateUserMutation } from '@app/api/User/api';
import type { PhoneValidation } from '@app/components/Phone/types';
import {
  SUCCESS,
  ERROR,
  WARNING,
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
} from '@app/constants';
import {
  useMemoizeObject,
  useUnsavedData,
  useUserPrint,
  useUsersAccessRights,
} from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useAppSelector, selectCountries } from '@app/store';
import type { NEmployees, NSmallGlobalEntities } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NDictionaries } from '@app/types/Dictionaries';
import type { NUtils } from '@app/types/Utils';
import { afterUserUpdate } from '@app/utils';

import type { AddEmployeeFormValues } from '@pages/Employees/AddEmployee/types';

const ADD_EMPLOYEE = 'addEmployee';
const { useForm, useWatch } = Form;

export const useAddEmployee = () => {
  const { userId, database, activeBranchId, activeBranchCountryId } =
    useUserPrint();
  const {
    hasUserRightToCreate: hasUserRightToCreateUser,
    hasUserRightToCreatePosition,
    hasUserRightToChangeBranches,
  } = useUsersAccessRights();

  const [isFetchingLoading, setIsFetchingLoading] = useState(false);
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [isSavingLoading, setIsSavingLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<NUtils.ErrorTypes>(undefined);
  const [open, setOpen] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const [allBranches, setAllBranches] = useState<NBranches.ModelBranchItem[]>(
    []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = useForm();
  const [modalForm] = useForm();

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [updatePosition] = useUpdatePositionMutation();
  const [updateUser] = useUpdateUserMutation();

  const { showNotification } = useContext(NotificationContext);

  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });

  const navigate = useNavigate();

  const [, contextHolder] = notification.useNotification();

  const countries = useAppSelector(selectCountries);
  const {
    setNewData,
    prepareStartedData,
    checkIsDataUpdated,
    newData,
    setInputData,
    setSelectorData,
    setDatePickerData,
  } = useContext(LeavePageContext);

  const memoizedAllBranches = useMemoizeObject(allBranches);
  const memoizedSelectedBranches = useMemoizeObject(selectedBranches);
  const memoizedCountries = useMemoizeObject(countries);

  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingLoading(true);
      if (!activeBranchId) return;

      try {
        if (hasUserRightToCreateUser) {
          const positionsResponse = await getPositionsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setPositions([
            { Id: 0, IdS: null, Name: 'Без должности' },
            ...positionsResponse,
          ]);
        }

        const branchesResponse = await getBranchesForSystem({
          ActiveBranchId: activeBranchId,
        }).unwrap();
        setAllBranches(branchesResponse);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setIsFetchingLoading(false);
      }
    };

    fetchData();
  }, [
    activeBranchId,
    database,
    userId,
    hasUserRightToCreateUser,
    form,
    showNotification,
    getBranchesForSystem,
    getPositionsForSystem,
  ]);

  useEffect(() => {
    const currentBranch = (memoizedAllBranches || []).find(
      ({ Id }) => Id === activeBranchId
    );
    if (
      !(memoizedSelectedBranches || []).find(
        ({ Id: localId }) => localId === activeBranchId
      )
    ) {
      currentBranch &&
        setSelectedBranches((prev) => prev.concat(currentBranch));

      const countryObj = (memoizedCountries || []).find(
        (item: NDictionaries.ModelCountry) =>
          +item.TelCode === +(activeBranchCountryId || 0)
      );

      prepareStartedData({
        LastName: undefined,
        FirstName: undefined,
        MiddleName: undefined,
        Email: undefined,
        Password: undefined,
        Phone: '',
        PhoneCountryId: countryObj?.Id,
        Position: undefined,
        Gender: undefined,
        Date: null,
        Description: undefined,
        selectedBranches: [currentBranch?.Id],
      });
    }
  }, [
    activeBranchId,
    activeBranchCountryId,
    memoizedAllBranches,
    memoizedSelectedBranches,
    memoizedCountries,
    prepareStartedData,
  ]);

  const showUserModal = () => {
    setOpen(true);
  };

  const hideUserModal = () => {
    setOpen(false);
  };

  const observedData = useWatch([], form);

  const { saveData } = useUnsavedData({ data: observedData });

  const onFinish = useCallback(
    async ({
      NameFirst,
      NameLast,
      NameMiddle,
      Position,
      Gender,
      Description = '',
      Email,
      Password,
      DateBirth,
      Phone: { Phone, CountryId },
      Note = '',
      Snils,
      SignerRole,
      PositionCode,
      SpecialityCodeNsi,
      TelegramId,
    }: AddEmployeeFormValues) => {
      const { errorMessage, isValid } = phoneValidation;

      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        form.setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return;
      }

      const data = {
        Id: 0,
        NameFirst,
        NameLast: NameLast || null,
        NameMiddle: NameMiddle || null,
        Email: Email || null,
        Password: Password || null,
        Phone,
        PhoneCountryId: CountryId,
        PositionId: Position === 0 || !Position ? null : Position,
        Gender: Gender || null,
        DateBirth: DateBirth
          ? DateBirth.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
          : null,
        Description,
        Note,
        IsEmployee: true,
        IsSaveAdditions: true,
        UserBranchIds: selectedBranches.length
          ? selectedBranches.filter((item) => item !== null).map(({ Id }) => Id)
          : null,
        PhoneCode: null,
        IsDeleted: false,
        IsAdmin: false,
        Photo: null,
        AlterIsEmployee: null,
        DateWorkStart: null,
        DatePatentEnd: null,
        PassportInfo: null,
        TaxPayerId: null,
        InsuranceCertificateN: null,
        Citizenship: null,
        IsTerminated: false,
        MigrateId: null,
        IsFakeEmail: false,
        IsFakePhone: false,
        Snils,
        SignerRole,
        PositionCode,
        SpecialityCodeNsi,
        TelegramId: TelegramId || null,
      };

      if (userId && database && activeBranchId) {
        try {
          setIsSavingLoading(true);

          const { Id: createdUserId } = await updateUser({
            data,
            ActiveBranchId: activeBranchId,
            logs: { isLogged: true, userId, database },
          }).unwrap();

          showNotification('Пользователь создан успешно', SUCCESS);
          prepareStartedData({});
          saveData();
          afterUserUpdate({
            id: data.Id,
            createdUserId,
            navigate,
            submitType: ADD_EMPLOYEE,
            form,
          });
        } catch (error) {
          setErrorType(ERROR);
          setError(handleCatchError(error));
        } finally {
          setIsSavingLoading(false);
        }
      }
    },
    [
      userId,
      database,
      activeBranchId,
      selectedBranches,
      form,
      setError,
      prepareStartedData,
      setErrorType,
      saveData,
      navigate,
      showNotification,
      phoneValidation,
      updateUser,
    ]
  );

  const addPosition = () => {
    showModal();
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    modalForm.submit();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    modalForm.resetFields();
    setIsModalOpen(false);
  };

  const finishModal: NEmployees.FinishModal = async ({ Name, Description }) => {
    try {
      await updatePosition({
        ActiveBranchId: activeBranchId,
        data: {
          Id: 0,
          Name,
          Description,
          IsDeleted: false,
          MigrateId: null,
        },
      }).unwrap();

      showNotification('Должность успешно добавлена', SUCCESS);

      if (hasUserRightToCreateUser) {
        const positionsResponse = await getPositionsForSystem({
          ActiveBranchId: activeBranchId,
        }).unwrap();
        const positionsWithEmpty = [
          { Id: 0, IdS: null, Name: 'Без должности' },
          ...positionsResponse,
        ];
        setPositions(positionsWithEmpty);

        const position = positionsWithEmpty.find(
          ({ Name: localName }) => localName === Name
        );

        if (position) {
          form.setFieldValue('Position', position.Id);

          setNewData({
            ...newData,
            Position: position.Id,
          });
        }
      }
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
      setError(handleCatchError(error));
    }
  };

  const handleCancelSaving = () => {
    navigate('/employees');
    checkIsDataUpdated({
      path: '/employees',
      navigate,
    });
  };

  const initialValues = useMemo(
    () => ({
      NameLast: '',
      NameFirst: '',
      NameMiddle: '',
      DateBirth: null,
      Description: '',
      Email: '',
      Password: '',
      Gender: '',
      Position: '',
      Phone: {
        Phone: '',
        CountryId: activeBranchCountryId || null,
      },
      Snils: null,
      SignerRole: null,
      SpecialityCodeNsi: null,
      TelegramId: null,
    }),
    [activeBranchCountryId]
  );

  return {
    isFetchingLoading,
    form,
    onFinish,
    positions,
    error,
    errorType,
    setError,
    open,
    selectedBranches,
    setSelectedBranches,
    showUserModal,
    hideUserModal,
    allBranches,
    contextHolder,
    addPosition,
    handleOk,
    handleCancel,
    isModalOpen,
    modalForm,
    finishModal,
    handleCancelSaving,
    setNewData,
    hasUserRightToCreateUser,
    hasUserRightToCreatePosition,
    hasUserRightToChangeBranches,
    setInputData,
    setSelectorData,
    setDatePickerData,
    initialValues,
    isSavingLoading,
    setPhoneValidation,
    phoneValidation,
  };
};
